/** @file World affine a slot's region or rigid mesh uses: slot bone (or override) times the active mount. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { Pose, SkeletonData } from '@pose2d/core/types';
import { affine, type Affine } from '@pose2d/math/affine';
import { invariant } from '@pose2d/std/invariant';

const WS = POSE_LAYOUT.WORLD_STRIDE;
/** Copy of the slot's mount block: affine.mul takes whole affines, not offsets (subarray would allocate). */
const mount = new Float64Array(WS);

/**
 * Writes into `out` the world of `slotBoneOverride[slot]` when ≥ 0, else of `SlotData.bone`, times `slotMount[slot]`
 * when `slotMountActive[slot] === 1` (W2-21, spec §6.14); returns `out`. Skinned meshes do not use it (influence
 * bones only); `pose.flipX` is never applied. Allocation-free; throws (invariant) for a slot outside `data.slots`.
 */
export function slotWorld(pose: Pose, data: SkeletonData, slot: number, out: Affine): Affine {
  const slotData = data.slots[slot];
  invariant(slotData !== undefined, 'slotWorld: slot index out of range');
  const override = pose.slotBoneOverride[slot] ?? -1;
  const o = (override >= 0 ? override : slotData.bone) * WS;
  for (let j = 0; j < WS; j++) out[j] = pose.world[o + j] ?? 0;
  if (pose.slotMountActive[slot] === 1) {
    for (let j = 0; j < WS; j++) mount[j] = pose.slotMount[slot * WS + j] ?? 0;
    affine.mul(out, out, mount);
  }
  return out;
}
